/**
 * Pure pieces of column visibility: which id TanStack will use for a column,
 * what to call it in the manager, which columns can be managed at all, and
 * what a table shows by default at a given width. No React, so the checks run
 * in node.
 *
 * Ported from glp-frontend `components/helpers/data-table/columnVisibility.ts`.
 */
import type { ColumnDef, VisibilityState } from '@tanstack/react-table';

import './column-meta';

/** Below Tailwind's `sm`: a phone. A table starts with its primary columns only. */
export const COMPACT_TABLE_QUERY = '(max-width: 639px)';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyColumn = ColumnDef<any, any>;

/** The id TanStack resolves for a column: an explicit id, else the accessor key. */
export function columnKey(column: AnyColumn): string {
  if (column.id) return column.id;
  const key = (column as { accessorKey?: unknown }).accessorKey;
  return typeof key === 'string' ? key : '';
}

/** What the manager calls a column: meta label, a string header, else the key. */
export function columnLabel(column: AnyColumn): string {
  const label = column.meta?.label;
  if (label) return label;
  if (typeof column.header === 'string' && column.header.trim()) return column.header;
  return columnKey(column);
}

/** Columns a user may hide. Locked ones (selection, actions) are never offered. */
export function manageableColumns<T extends AnyColumn>(columns: T[]): T[] {
  return columns.filter((column) => !column.meta?.locked);
}

/**
 * What a table shows before the user touches anything. On a compact screen
 * only primary and locked columns; otherwise everything.
 */
export function defaultVisibility(columns: AnyColumn[], compact: boolean): VisibilityState {
  const visibility: VisibilityState = {};
  for (const column of columns) {
    const key = columnKey(column);
    if (!key) continue;
    if (!compact) {
      visibility[key] = true;
    } else {
      visibility[key] = Boolean(column.meta?.primary || column.meta?.locked);
    }
  }
  return visibility;
}

/** Locked columns stay visible whatever the caller asked for. */
export function withLockedVisible(
  columns: AnyColumn[],
  visibility: VisibilityState,
): VisibilityState {
  const next = { ...visibility };
  for (const column of columns) {
    if (column.meta?.locked) next[columnKey(column)] = true;
  }
  return next;
}
